/**
 * The setup harness on the wire (Phase 3 of docs/plans/onboarding-harness.md):
 * what a setup turn carries beside its messages when it runs on a server-held
 * session, the data parts the turn streams back, and the readers a client
 * draws them with.
 *
 * A turn rides `POST /v1/agent/chat` with `context.screen: 'setup'` and a
 * `context.session`; without one, the turn keeps the legacy path. A message is
 * the AI SDK's `UIMessage`, read here structurally: this package does not
 * depend on `ai` (the reason `compose.ts` gives).
 *
 * Nothing here reads partial tool input: a call arrives whole, and nothing is
 * drawn before the server accepts a version.
 *
 * Pure: the phone bundles it.
 */
import type { ApiError } from './index';
import type { SetupOpsResponse, StudioOp, StudioPartRef, StudioReceipt } from './setup-draft';
import { studioRefOf } from './setup-draft/paths';
import type { SetupGap } from './setup-gaps';
import type { SetupReadiness } from './setup-readiness';

/** Parts one turn may select and talk about. */
export const SETUP_SCOPE_MAX = 8;
/** Questions on one card. */
export const SETUP_ASK_SET_MAX = 4;
/** Chips on one question. */
export const SETUP_ASK_CHIPS_MAX = 6;
/** A meter warns when this many or fewer are left. */
export const SETUP_QUOTA_WARN_AT = 5;
/** Changes in one `note_changes`: a whole first turn fits, as new records and lists may share one change. */
export const SETUP_CHANGES_MAX = 16;

/* ------------------------------------------------------------ the turn's context */

/** A part the person selected on the canvas, by its part path, at the version they saw it. */
export type DraftScope = { readonly path: string; readonly version: number };

export type SetupTurnContext = {
  readonly sessionId: string;
  /** The head version the person saw when they wrote. */
  readonly baseVersion: number;
  readonly scope?: readonly DraftScope[];
  /** The question card this turn answers. */
  readonly answersFor?: string;
  /**
   * The open check this turn's line answers, by its gap id: the line was an
   * answer picked on the checks sheet (16 Oct), so the turn settles it.
   */
  readonly answersCheck?: string;
  /**
   * The check's words as the person saw it when they answered (C2 of
   * docs/plans/checks-in-thread.md): a frozen copy the line keeps, so its
   * card reads right after a reload, a retry, or once the check has gone.
   * Kept only beside `answersCheck`; a line from before it has none.
   */
  readonly checkWords?: SetupCheckWords;
  /**
   * The tray's "Look things up on the web" switched off (step 5.13 of
   * docs/plans/onboarding-harness/phase-05.md, decision 7): sent only when
   * off, so a turn with no word keeps looking things up.
   */
  readonly web?: false;
};

/** A check's words, frozen on the line that answers it: its title, its question and the part's name, each null when it has none. */
export type SetupCheckWords = { readonly title: string | null; readonly question: string | null; readonly on: string | null };

export const CHECK_WORDS_TITLE_MAX = 200;
export const CHECK_WORDS_QUESTION_MAX = 400;
export const CHECK_WORDS_ON_MAX = 120;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SCOPE_PATH_MAX = 200;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const uuidOf = (value: unknown): string | undefined => (typeof value === 'string' && UUID.test(value) ? value.toLowerCase() : undefined);
/** A gap's id as the wire carries it: `rule:key` characters only. */
const GAP_ID = /^[A-Za-z0-9_.:-]{1,160}$/;
const gapIdOf = (value: unknown): string | undefined => (typeof value === 'string' && GAP_ID.test(value) ? value : undefined);

/** One word field: control characters and runs of space made one space, trimmed, cut at `max`; empty or not a string is null. */
const wordsOf = (value: unknown, max: number): string | null => {
  if (typeof value !== 'string') return null;
  const flat = value.replace(/[\u0000-\u001f\u007f\s]+/g, ' ').trim();
  return flat === '' ? null : flat.slice(0, max).trimEnd();
};

/**
 * A check's frozen words, read loosely: each field read and capped alone; a
 * copy with neither a title nor a question says nothing, and is dropped.
 */
export const readCheckWords = (raw: unknown): SetupCheckWords | undefined => {
  if (!isRecord(raw)) return undefined;
  const title = wordsOf(raw.title, CHECK_WORDS_TITLE_MAX);
  const question = wordsOf(raw.question, CHECK_WORDS_QUESTION_MAX);
  if (title === null && question === null) return undefined;
  return { title, question, on: wordsOf(raw.on, CHECK_WORDS_ON_MAX) };
};

/**
 * The check a thread line answers, from its metadata (`answersCheck`, and the
 * `checkWords` it froze): null when it answers none. A line stored before the
 * words rode on it reads with `words: null`.
 */
export const lineCheckOf = (metadata: unknown): { readonly id: string; readonly words: SetupCheckWords | null } | null => {
  if (!isRecord(metadata)) return null;
  const id = gapIdOf(metadata.answersCheck);
  if (id === undefined) return null;
  return { id, words: readCheckWords(metadata.checkWords) ?? null };
};

/** The words of a person's line a direct fix records. */
export const SETUP_SAID_TEXT_MAX = 2000;

/**
 * The person's line a fix pressed on Things to check records in the thread
 * (C1 of docs/plans/checks-in-thread.md): the answer's words, the check it
 * answers and the check's words as shown, sent with the fix's ops
 * (`POST …/ops`) and stored as a line of the thread with no turn, so it is
 * still there after a reload. `id` is the message's id, made once, so a retry
 * of the fix stores it once.
 */
export type SetupSaidLine = {
  readonly id: string;
  readonly text: string;
  readonly answersCheck: string;
  readonly checkWords?: SetupCheckWords;
};

/** A direct fix's line, read loosely: null when it has no message id, no words or no check; its words capped. */
export const readSaidLine = (raw: unknown): SetupSaidLine | null => {
  if (!isRecord(raw)) return null;
  const id = uuidOf(raw.id);
  const answersCheck = gapIdOf(raw.answersCheck);
  const text = typeof raw.text === 'string' ? raw.text.trim().slice(0, SETUP_SAID_TEXT_MAX) : '';
  if (id === undefined || answersCheck === undefined || text === '') return null;
  const checkWords = readCheckWords(raw.checkWords);
  return { id, text, answersCheck, ...(checkWords === undefined ? {} : { checkWords }) };
};

/**
 * A direct fix's line as the thread holds it: the person's message, its
 * metadata the check it answers, the check's words, and `direct: true` (no
 * turn was run for it).
 */
export const saidLineMessage = (
  line: SetupSaidLine,
): { readonly id: string; readonly role: 'user'; readonly parts: readonly { readonly type: 'text'; readonly text: string }[]; readonly metadata: Record<string, unknown> } => ({
  id: line.id,
  role: 'user',
  parts: [{ type: 'text', text: line.text }],
  metadata: { answersCheck: line.answersCheck, ...(line.checkWords === undefined ? {} : { checkWords: line.checkWords }), direct: true },
});

const readScope = (raw: unknown): DraftScope[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const kept: DraftScope[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (kept.length >= SETUP_SCOPE_MAX) break;
    if (!isRecord(item)) continue;
    const { path, version } = item;
    if (typeof path !== 'string' || path.length > SCOPE_PATH_MAX || !isCount(version)) continue;
    if (seen.has(path) || studioRefOf(path) === null) continue;
    seen.add(path);
    kept.push({ path, version });
  }
  return kept.length === 0 ? undefined : kept;
};

/**
 * A setup turn's session context, read loosely like the rest of the screen:
 * a bad session id or base version drops it whole (the turn then keeps the
 * legacy path); a bad scope item, `answersFor`, `answersCheck` or `checkWords` is
 * dropped alone, and `checkWords` rides only beside `answersCheck`.
 */
export const readSetupTurnContext = (raw: unknown): SetupTurnContext | undefined => {
  if (!isRecord(raw)) return undefined;
  const sessionId = uuidOf(raw.sessionId);
  if (sessionId === undefined || !isCount(raw.baseVersion)) return undefined;
  const scope = readScope(raw.scope);
  const answersFor = uuidOf(raw.answersFor);
  const answersCheck = gapIdOf(raw.answersCheck);
  const checkWords = answersCheck === undefined ? undefined : readCheckWords(raw.checkWords);
  return {
    sessionId,
    baseVersion: raw.baseVersion,
    ...(scope === undefined ? {} : { scope }),
    ...(answersFor === undefined ? {} : { answersFor }),
    ...(answersCheck === undefined ? {} : { answersCheck }),
    ...(checkWords === undefined ? {} : { checkWords }),
    ...(raw.web === false ? { web: false as const } : {}),
  };
};

/* --------------------------------------------------------------- the stream parts */

export const SETUP_TURN_PARTS = {
  draft: 'data-setup-draft',
  refused: 'data-setup-refused',
  looked: 'data-setup-looked',
  status: 'data-setup-status',
  ask: 'data-setup-ask',
  notYet: 'data-setup-not-yet',
  quota: 'data-setup-quota',
} as const;

/** A version the server accepted, as the turn streams it. Part id `draft-v<version>`. */
export type SetupDraftPart = {
  readonly version: number;
  readonly baseVersion: number;
  readonly actor: 'agent' | 'person' | 'system';
  readonly reason: string;
  /** Which compile pass made it; null outside a compile. */
  readonly pass: 'names' | 'fields' | 'wires' | null;
  readonly turnId: string | null;
  /** The first version this turn made: the receipt's Undo restores the one before it. */
  readonly turnFirstVersion: number | null;
  readonly ops: readonly StudioOp[];
  readonly receipt: StudioReceipt;
  readonly alsoTouched: readonly StudioPartRef[];
  readonly scopeSpent: boolean;
  readonly readiness: SetupReadiness;
  readonly gaps: readonly SetupGap[];
};

/** The part id of a version's draft part. */
export const setupDraftPartId = (version: number): string => `draft-v${version}`;

/** A change the compiler could not make: the studio says "Could not add that" on the parts it names. */
export type SetupRefusedPart = {
  /** Its index in the turn's changes. */
  readonly change: number;
  readonly says: string;
  readonly words: readonly string[];
  readonly refs: readonly StudioPartRef[];
};

export const SETUP_LOOKED_FAILURES = [
  'unreachable',
  'blocked',
  'not_text',
  'too_big',
  'quota',
  'unavailable',
  'not_named',
  'private',
  'not_found',
  'app_named',
] as const;
export type SetupLookedFailure = (typeof SETUP_LOOKED_FAILURES)[number];

/**
 * A lookup's chip. The same id is written again as its state moves; a lookup
 * starts in one message and lands in a later one. An app's research carries
 * `topicKey`, never `topic`.
 */
export type SetupLookedPart = {
  readonly id: string;
  readonly kind: 'web' | 'page' | 'place' | 'app';
  readonly state: 'looking' | 'done' | 'failed';
  readonly topic?: string;
  readonly topicKey?: 'setup.studio.looked.app';
  readonly sources: readonly { readonly title: string; readonly host: string; readonly url: string | null; readonly appSite?: boolean }[];
  /** Google's `searchEntryPoint.renderedContent`, exactly as served; null when no search ran. */
  readonly suggestions: string | null;
  readonly failure?: SetupLookedFailure;
  /** An app looked up with little found (6.14, board `sources-unknown`): the card offers a screenshot, an export or a question instead. */
  readonly thin?: true;
};

/**
 * The working line, written transient (never stored): what OS is doing at that
 * moment (Devendra, 16 Oct). Thinking while it thinks or answers; a lookup's
 * topic (an app is never named); the source it reads; Drawing with the new
 * parts' names only while new parts are drawn; Changing with the part's name
 * while existing ones change; Checking while the draft is checked.
 */
export const SETUP_STATUS_KEYS = [
  'setup.studio.working.thinking',
  'setup.studio.working.looking',
  'setup.studio.working.looking_web',
  'setup.studio.working.looking_app',
  'setup.studio.working.reading',
  'setup.studio.working.adding',
  'setup.studio.working.on',
  'setup.studio.working.checking',
] as const;

export type SetupStatusPart = {
  readonly key: (typeof SETUP_STATUS_KEYS)[number];
  readonly vars: Readonly<Record<string, string>>;
  /** Part paths of accepted versions the compiler is still filling in; [] when none. */
  readonly drawing: readonly string[];
};

/** How a question's free answer is typed. */
export const SETUP_ASK_INPUT_HINTS = ['text', 'url', 'number', 'phone'] as const;

/**
 * One question of a card, as a client draws it. A chip's ops stay on the
 * server: `hasFix` says picking it changes the draft with no model call.
 * `preview` is drawn, never applied, until its chip is picked. A chip from a
 * gap carries its `labelKey` and `vars`, for the client's own words.
 */
export type SetupAskQuestion = {
  readonly question: string;
  readonly why: string | null;
  readonly pick: 'one' | 'many';
  readonly chips: readonly {
    readonly label: string;
    readonly hasFix: boolean;
    readonly labelKey?: string;
    readonly vars?: Readonly<Record<string, string>>;
  }[];
  readonly decide: boolean;
  readonly other: boolean;
  readonly gapId: string | null;
  readonly placeId: string | null;
  readonly about: StudioPartRef | null;
  readonly inputHint: (typeof SETUP_ASK_INPUT_HINTS)[number] | null;
  readonly preview: readonly StudioOp[] | null;
};

/** What the person did with one question. */
export type SetupAskAnswer =
  | { readonly kind: 'chips'; readonly chips: readonly number[] }
  | { readonly kind: 'decide' }
  | { readonly kind: 'other'; readonly text: string }
  | { readonly kind: 'skip' };

export const SETUP_ASK_STATUSES = ['open', 'done', 'withdrawn'] as const;

/** A question card: its questions, the answers so far, which one it shows, and whether it is still open. */
export type SetupAskState = {
  readonly askId: string;
  readonly questions: readonly SetupAskQuestion[];
  readonly answers: readonly (SetupAskAnswer | null)[];
  readonly at: number;
  readonly status: (typeof SETUP_ASK_STATUSES)[number];
};
export type SetupAskPart = SetupAskState;

/**
 * The answer route's body: the card as it is now, the version an answer's
 * ready fix made (null when it made none), and whether OS's next turn is due.
 */
export type SetupAskAnswered = {
  readonly ask: SetupAskState;
  readonly batch: (SetupOpsResponse & { readonly gaps: readonly SetupGap[]; readonly readiness: SetupReadiness }) | null;
  readonly turn: 'none' | 'due';
};

/** `POST /v1/setup/sessions/:id/asks/:askId/answers`. */
export type SetupAskAnswerRequest = { readonly index: number; readonly answer: SetupAskAnswer; readonly baseVersion: number };

export type SetupNotYetPart = {
  readonly need: string;
  readonly fit: string;
  readonly capability: string | null;
  readonly refs: readonly StudioPartRef[];
};

export const SETUP_METER_NAMES = ['turns', 'searches', 'pages', 'places', 'apps', 'critic'] as const;
export type SetupMeterName = (typeof SETUP_METER_NAMES)[number];

/** How much of a meter is left; `critic` is never shown (spent skips the critic). */
export type SetupQuotaState = {
  readonly meter: SetupMeterName;
  readonly state: 'warn' | 'spent';
  readonly left: number;
  /** ISO. */
  readonly resetsAt: string;
};
export type SetupQuotaPart = SetupQuotaState;

/** A turn refused because a meter is spent. */
export type SetupQuotaRefusal = ApiError & { readonly quota: SetupQuotaState };

/* ------------------------------------------------------------------- the readers */

/** A message as this package reads it: the AI SDK's `UIMessage`, structurally. */
export type SetupMessageLike = { readonly parts?: readonly unknown[]; readonly metadata?: unknown };

export type SetupHarnessParts = {
  drafts: SetupDraftPart[];
  refused: SetupRefusedPart[];
  looked: SetupLookedPart[];
  asks: SetupAskPart[];
  notYet: SetupNotYetPart[];
  quota: SetupQuotaPart | null;
};

const isText = (value: unknown): value is string => typeof value === 'string';
const isTextList = (value: unknown): value is string[] => Array.isArray(value) && value.every(isText);
const isRefList = (value: unknown): value is StudioPartRef[] => Array.isArray(value) && value.every((one) => isRecord(one) && isText(one.kind));
const oneOf = <T extends string>(list: readonly T[], value: unknown): value is T => isText(value) && (list as readonly string[]).includes(value);

const isDraftPart = (data: Record<string, unknown>): boolean =>
  isCount(data.version) &&
  isCount(data.baseVersion) &&
  oneOf(['agent', 'person', 'system'], data.actor) &&
  isText(data.reason) &&
  (data.pass === null || oneOf(['names', 'fields', 'wires'], data.pass)) &&
  (data.turnId === null || isText(data.turnId)) &&
  (data.turnFirstVersion === null || isCount(data.turnFirstVersion)) &&
  Array.isArray(data.ops) &&
  isRecord(data.receipt) &&
  isRefList(data.alsoTouched) &&
  typeof data.scopeSpent === 'boolean' &&
  isRecord(data.readiness) &&
  oneOf(['not_ready', 'check', 'ready'], data.readiness.state) &&
  Array.isArray(data.gaps);

const isRefusedPart = (data: Record<string, unknown>): boolean =>
  isCount(data.change) && isText(data.says) && isTextList(data.words) && isRefList(data.refs);

const isLookedPart = (data: Record<string, unknown>): boolean =>
  isText(data.id) &&
  data.id !== '' &&
  oneOf(['web', 'page', 'place', 'app'], data.kind) &&
  oneOf(['looking', 'done', 'failed'], data.state) &&
  (data.topic === undefined || isText(data.topic)) &&
  (data.topicKey === undefined || data.topicKey === 'setup.studio.looked.app') &&
  Array.isArray(data.sources) &&
  data.sources.every((one) => isRecord(one) && isText(one.title) && isText(one.host) && (one.url === null || isText(one.url))) &&
  (data.suggestions === null || isText(data.suggestions)) &&
  (data.failure === undefined || oneOf(SETUP_LOOKED_FAILURES, data.failure));

const isAskPart = (data: Record<string, unknown>): boolean =>
  isText(data.askId) &&
  data.askId !== '' &&
  Array.isArray(data.questions) &&
  data.questions.every((one) => isRecord(one) && isText(one.question) && Array.isArray(one.chips)) &&
  Array.isArray(data.answers) &&
  isCount(data.at) &&
  oneOf(SETUP_ASK_STATUSES, data.status);

const isNotYetPart = (data: Record<string, unknown>): boolean =>
  isText(data.need) && isText(data.fit) && (data.capability === null || isText(data.capability)) && isRefList(data.refs);

/** A quota part names a meter the person sees: never the critic. */
const isQuotaPart = (data: Record<string, unknown>): boolean =>
  oneOf(SETUP_METER_NAMES, data.meter) &&
  data.meter !== 'critic' && oneOf(['warn', 'spent'], data.state) && isCount(data.left) && isText(data.resetsAt);

/** One data part of the harness, or null: its type, its id (when written with one) and its data. */
const dataPartOf = (raw: unknown): { type: string; id: string | null; data: Record<string, unknown> } | null => {
  if (!isRecord(raw) || !isText(raw.type) || !isRecord(raw.data)) return null;
  return { type: raw.type, id: isText(raw.id) ? raw.id : null, data: raw.data };
};

/** Last write per id wins; a part written with no id is its own entry. */
const lastPerId = <T>(entries: Array<{ id: string | null; value: T }>): T[] => {
  const order: Array<string | symbol> = [];
  const byId = new Map<string | symbol, T>();
  for (const entry of entries) {
    const id = entry.id ?? Symbol('part');
    if (!byId.has(id)) order.push(id);
    byId.set(id, entry.value);
  }
  return order.map((id) => byId.get(id)!);
};

/**
 * The harness parts of one message: last write per id wins, malformed parts
 * are skipped, and it never throws.
 */
export const harnessPartsOf = (message: SetupMessageLike): SetupHarnessParts => {
  const drafts: Array<{ id: string | null; value: SetupDraftPart }> = [];
  const refused: Array<{ id: string | null; value: SetupRefusedPart }> = [];
  const looked: Array<{ id: string | null; value: SetupLookedPart }> = [];
  const asks: Array<{ id: string | null; value: SetupAskPart }> = [];
  const notYet: Array<{ id: string | null; value: SetupNotYetPart }> = [];
  let quota: SetupQuotaPart | null = null;
  const parts = isRecord(message) && Array.isArray(message.parts) ? message.parts : [];
  for (const raw of parts) {
    const part = dataPartOf(raw);
    if (part === null) continue;
    const { type, data } = part;
    switch (type) {
      case SETUP_TURN_PARTS.draft:
        if (isDraftPart(data)) drafts.push({ id: part.id ?? setupDraftPartId(data.version as number), value: data as unknown as SetupDraftPart });
        break;
      case SETUP_TURN_PARTS.refused:
        if (isRefusedPart(data)) refused.push({ id: part.id, value: data as unknown as SetupRefusedPart });
        break;
      case SETUP_TURN_PARTS.looked:
        if (isLookedPart(data)) looked.push({ id: part.id ?? (data.id as string), value: data as unknown as SetupLookedPart });
        break;
      case SETUP_TURN_PARTS.ask:
        if (isAskPart(data)) asks.push({ id: part.id ?? (data.askId as string), value: data as unknown as SetupAskPart });
        break;
      case SETUP_TURN_PARTS.notYet:
        if (isNotYetPart(data)) notYet.push({ id: part.id, value: data as unknown as SetupNotYetPart });
        break;
      case SETUP_TURN_PARTS.quota:
        if (isQuotaPart(data)) quota = data as unknown as SetupQuotaPart;
        break;
      default:
        break;
    }
  }
  return {
    drafts: lastPerId(drafts),
    refused: lastPerId(refused),
    looked: lastPerId(looked),
    asks: lastPerId(asks),
    notYet: lastPerId(notYet),
    quota,
  };
};

/**
 * Across the thread, the last `done` or `failed` write of each lookup id: a
 * client draws a `looking` chip only until its lookup lands in a later message.
 */
export const landedLookups = (messages: readonly SetupMessageLike[]): Map<string, SetupLookedPart> => {
  const landed = new Map<string, SetupLookedPart>();
  for (const message of messages) {
    for (const part of harnessPartsOf(message).looked) {
      if (part.state !== 'looking') landed.set(part.id, part);
    }
  }
  return landed;
};

/** The question card an answer message answers (`metadata.answersFor`), or null. */
export const answersForOf = (message: SetupMessageLike): string | null => {
  const metadata = isRecord(message) ? message.metadata : undefined;
  return isRecord(metadata) ? (uuidOf(metadata.answersFor) ?? null) : null;
};
